import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import { Add, Edit, Delete, Search, Visibility, OpenInNew, Download, Upload } from '@mui/icons-material';
import { transactionsApi, TransactionGridItem, TransactionWithDetails, TransactionItem } from '../services/transactions';
import { companiesApi, CompanyList } from '../services/companies';
import { agreementsApi, AgreementList } from '../services/agreements';
import { servicesApi, Service } from '../services/services';
import { companyUsersApi } from '../services/companyUsers';
import { companyWalletsApi, CompanyWallet } from '../services/companyWallets';
import { BulkGenerateInvoicesDialog } from '../components/BulkGenerateInvoicesDialog';
import { TransactionReportDialog } from '../components/TransactionReportDialog';
import { InvoiceDocumentPreview } from '../components/InvoiceDocumentPreview';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { isAgreementSelectable } from '../utils/agreementActive';

function formatApiError(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object' && 'msg' in (detail as object)) return (detail as any).msg;
  return typeof detail === 'object' ? JSON.stringify(detail) : String(detail ?? 'Error');
}

/** Sandbox-компания для CSM (должна совпадать с CSM_SANDBOX_COMPANY_NAME на бэкенде). */
const CSM_SANDBOX_COMPANY_NAME = 'test';

function companiesForCsm(list: CompanyList[]): CompanyList[] {
  const want = CSM_SANDBOX_COMPANY_NAME.toLowerCase();
  const exact = list.filter((c) => (c.name || '').trim().toLowerCase() === want);
  if (exact.length > 0) return exact;
  return list.filter((c) => (c.name || '').trim().toLowerCase().includes(want));
}

function lineQuantity(item: { quantity?: number }): number {
  const q = item.quantity;
  if (typeof q === 'number' && !Number.isNaN(q) && q > 0) return q;
  const p = parseFloat(String(q ?? 1));
  return p > 0 ? p : 1;
}

interface TransactionForm {
  number: string;
  type: 'prepayment' | 'invoice';
  status: 'new' | 'payed' | 'approved' | 'canceled';
  company_id?: number;
  agreement_id?: number;
  total: number;
  confirmation_document_url?: string; // Теперь это массив URL, хранится как JSON строка
  confirmation_document_file?: string;
  field1?: string;
  field2?: string;
  field3?: string;
  field4?: string;
  field5?: string;
  transaction_items: TransactionItemForm[];
}

interface TransactionItemForm {
  agreement_to_service_rel_id: number;
  quantity: number;
  discount: number;      // скидка для invoice
  total_price?: number;  // для prepayment — итоговая сумма
  service_id?: number;
  service_name?: string;
}

function calculateTransactionTotal(
  formData: TransactionForm,
  agreementServiceRelations: { id: number; price?: number | string }[]
): number {
  return formData.transaction_items.reduce((sum, item) => {
    if (formData.type === 'prepayment') {
      const tp = item.total_price ?? 0;
      return sum + (typeof tp === 'number' ? tp : parseFloat(String(tp || 0)));
    }
    const rel = agreementServiceRelations.find(r => r.id === item.agreement_to_service_rel_id);
    const price = rel ? (typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0))) : 0;
    const discountVal = typeof item.discount === 'number' ? item.discount : parseFloat(String(item.discount || 0));
    const q = lineQuantity(item);
    return sum + Math.max(0, price * q - discountVal);
  }, 0);
}

function isNewTransactionFormIncomplete(formData: TransactionForm): boolean {
  if (formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id)) return true;
  if (formData.type === 'prepayment' && !formData.company_id) return true;
  if (
    formData.transaction_items.length > 0 &&
    formData.transaction_items.some(item => !item.agreement_to_service_rel_id || item.agreement_to_service_rel_id === 0)
  ) {
    return true;
  }
  return false;
}

function validateNewTransactionForm(formData: TransactionForm): string | null {
  if (formData.type === 'invoice') {
    const hasSelectedService = formData.transaction_items.some(
      (item) => item.agreement_to_service_rel_id && Number(item.agreement_to_service_rel_id) > 0
    );
    if (!hasSelectedService) {
      return 'At least one service must be selected for the invoice.';
    }
  }
  if (isNewTransactionFormIncomplete(formData)) {
    return 'Please fill in all required fields before continuing.';
  }
  return null;
}

const TransactionsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const isClient = user?.role === 'client';
  const isCsmUser = user?.role === 'csm';
  /** CSM: только создание транзакций (sandbox), без списка и без остальных разделов */
  const txShellOnlyUser = isCsmUser;
  const canBulkGenerate =
    !isClient &&
    !isCsmUser &&
    user &&
    ['admin', 'employee', 'ae'].includes(user.role);
  const [transactions, setTransactions] = useState<TransactionGridItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  /** Пустая строка = без границы с этой стороны */
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isPrepaymentMode, setIsPrepaymentMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false); // Режим просмотра (только чтение)
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionWithDetails | null>(null);
  const [companies, setCompanies] = useState<CompanyList[]>([]);
  const [agreements, setAgreements] = useState<AgreementList[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [agreementServices, setAgreementServices] = useState<Service[]>([]); // Услуги, привязанные к выбранному договору
  const [agreementServiceRelations, setAgreementServiceRelations] = useState<any[]>([]); // Связи услуг с договором (для получения цен)
  const [formLoading, setFormLoading] = useState(false);
  const [isNewTransaction, setIsNewTransaction] = useState(false); // Флаг новой транзакции (не редактирование)
  const [confirmationFiles, setConfirmationFiles] = useState<File[]>([]); // Загружаемые файлы подтверждения
  const [confirmationDocuments, setConfirmationDocuments] = useState<string[]>([]); // Список ссылок на документы
  const [uploadingFile, setUploadingFile] = useState(false); // Флаг загрузки файла
  const [companyWallets, setCompanyWallets] = useState<CompanyWallet[]>([]); // Реквизиты компании
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [sendEmailOnCreate, setSendEmailOnCreate] = useState(false);
  const [showCreatePreview, setShowCreatePreview] = useState(false);

  const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    new: 'info',
    payed: 'success',
    approved: 'warning',
    canceled: 'error'
  };

  const statusLabels: Record<string, string> = {
    new: 'New',
    payed: 'Paid',
    approved: 'Approved',
    canceled: 'Canceled'
  };

  const typeLabels: Record<string, string> = {
    prepayment: 'Prepayment',
    invoice: 'Invoice'
  };

  // Form state
  const [formData, setFormData] = useState<TransactionForm>({
    number: '',
    type: 'invoice',
    status: 'new',
    company_id: undefined,
    confirmation_document_file: undefined,
    agreement_id: undefined,
    total: 0,
    confirmation_document_url: '',
    field1: '',
    field2: '',
    field3: '',
    field4: '',
    field5: '',
    transaction_items: []
  });

  const loadTransactions = async () => {
    if (user?.role === 'csm') {
      setTransactions([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const data = await transactionsApi.getTransactions({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: searchTerm.trim() || undefined,
        type: (filterType as 'invoice' | 'prepayment') || undefined,
        status: (filterStatus as 'new' | 'payed' | 'approved' | 'canceled') || undefined,
        createdFrom: filterDateFrom || undefined,
        createdTo: filterDateTo || undefined,
      });
      setTransactions(data.items);
      setTotalCount(data.total);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      setError(formatApiError(err.response?.data?.detail) || 'Error loading transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    void loadTransactions();
  }, [
    user?.role,
    user?.id,
    page,
    rowsPerPage,
    searchTerm,
    filterType,
    filterStatus,
    filterDateFrom,
    filterDateTo,
  ]);

  const loadCompanies = async () => {
    try {
      if (isClient && user?.id) {
        // Для клиентов загружаем только компании, привязанные к пользователю
        const companyUsers = await companyUsersApi.getCompanyUsersByUser(user.id);
        console.log('Loaded company users for client:', companyUsers);
        // Преобразуем в формат CompanyList
        const userCompanies: CompanyList[] = companyUsers.map(cu => ({
          id: cu.company_id,
          name: cu.company_name || `Company ${cu.company_id}`,
          country: '',
          created_at: '',
          updated_at: '',
          active: cu.is_active ?? true,
          balance: 0
        }));
        console.log('Mapped companies:', userCompanies);
        setCompanies(userCompanies);
      } else {
        const data = await companiesApi.getCompanies();
        const list = user?.role === 'csm' ? companiesForCsm(data) : data;
        setCompanies(list);
      }
    } catch (err: any) {
      console.error('Error loading companies:', err);
      console.error('Error details:', err.response?.data);
      setError(formatApiError(err.response?.data?.detail) || 'Error loading companies');
    }
  };

  const loadAgreements = async () => {
    try {
      if (isClient && formData.company_id) {
        // Для клиентов загружаем только договоры выбранной компании
        const data = await agreementsApi.getAgreementsByCompany(formData.company_id);
        setAgreements(data);
      } else {
        // Для админов и других ролей загружаем все договоры
        const data = await agreementsApi.getAgreements();
        setAgreements(data);
      }
    } catch (err: any) {
      console.error('Error loading agreements:', err);
    }
  };

  const loadServices = async () => {
    try {
      const data = await servicesApi.getServices(false);
      setServices(data);
    } catch (err: any) {
      console.error('Error loading services:', err);
    }
  };

  const loadAgreementServices = async (agreementId: number) => {
    try {
      const relations = await agreementsApi.getAgreementServiceRelations(agreementId);
      setAgreementServiceRelations(relations);
      const agreementServicesList: Service[] = [];
      for (const rel of relations) {
        if (rel.service) {
          agreementServicesList.push(rel.service);
        } else {
          const service = services.find(s => s.id === rel.service_id);
          if (service) {
            agreementServicesList.push(service);
          }
        }
      }
      setAgreementServices(agreementServicesList);
      return relations;
    } catch (err: any) {
      console.error('Error loading agreement services:', err);
      setAgreementServices([]);
      setAgreementServiceRelations([]);
      return [];
    }
  };

  const loadTransactionDetails = async (id: number) => {
    try {
      setFormLoading(true);
      const transaction = await transactionsApi.getTransaction(id);
      setSelectedTransaction(transaction);
      return transaction;
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Error loading transaction data');
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      return;
    }
    void loadCompanies();
    void loadServices();
  }, [user?.role, user?.id]);

  // Для клиентов перезагружаем договоры при изменении выбранной компании
  useEffect(() => {
    if (isClient && formData.company_id) {
      agreementsApi.getAgreementsByCompany(formData.company_id)
        .then(data => setAgreements(data))
        .catch(err => console.error('Error loading agreements:', err));
    } else if (user?.role === 'csm' && formData.company_id) {
      agreementsApi.getAgreementsByCompany(formData.company_id)
        .then(data => setAgreements(data))
        .catch(err => console.error('Error loading agreements:', err));
    } else if (!isClient) {
      loadAgreements();
    }
  }, [formData.company_id, isClient, user?.role]);

  const handleOpenTransaction = async (transactionId: number) => {
    const transaction = await loadTransactionDetails(transactionId);
    if (transaction) {
      setIsPrepaymentMode(false);
      setIsNewTransaction(false); // Это редактирование существующей транзакции
      setShowCreatePreview(false);
      setSendEmailOnCreate(false);
      // Проверяем права на редактирование
      // Клиенты могут редактировать только транзакции со статусом NEW
      const canEdit = !isClient || (isClient && transaction.status === 'new');
      setIsViewMode(!canEdit); // Если нет прав - режим просмотра
      
      // Загружаем связи договора, если есть договор
      if (transaction.agreement_id) {
        await loadAgreementServices(transaction.agreement_id);
      }
      
      // Загружаем реквизиты компании, если есть компания
      if (transaction.company_id) {
        try {
          const wallets = await companyWalletsApi.getCompanyWallets(transaction.company_id);
          setCompanyWallets(wallets);
        } catch (err: any) {
          console.error('Error loading company wallets:', err);
          setCompanyWallets([]);
        }
        
        // Загружаем договоры компании
        try {
          if (isClient) {
            // Для клиентов загружаем только договоры выбранной компании
            const agreementsData = await agreementsApi.getAgreementsByCompany(transaction.company_id);
            setAgreements(agreementsData);
          } else {
            // Для админов загружаем все договоры
            const agreementsData = await agreementsApi.getAgreements();
            setAgreements(agreementsData);
          }
        } catch (err: any) {
          console.error('Error loading agreements:', err);
        }
      } else {
        setCompanyWallets([]);
      }
      
      let transactionItems: TransactionItemForm[] = [];
      if (transaction.transaction_items) {
        transactionItems = transaction.transaction_items.map(item => {
          const relId = item.agreement_to_service_rel_id || (item.service_id ? -1 : 0);
          const service = item.agreement_to_service_rel?.service || item.service;
          const discount = typeof item.discount === 'number' ? item.discount : parseFloat(String(item.discount || 0));
          const totalPrice = typeof item.total_price === 'number' ? item.total_price : parseFloat(String(item.total_price || 0));
          return {
            agreement_to_service_rel_id: relId,
            service_id: service?.id || item.service_id,
            service_name: service?.name,
            quantity: lineQuantity(item),
            discount,
            total_price: transaction.type === 'prepayment' ? totalPrice : undefined
          };
        });
      }
      
      if (transaction.type === 'prepayment' && transaction.agreement_id && transactionItems.length === 0) {
        const prepaymentRel = agreementServiceRelations.find(rel => rel.service?.identifier === 'prepayment');
        if (prepaymentRel) {
          const total = typeof transaction.total === 'number' ? transaction.total : parseFloat(String(transaction.total || 0));
          transactionItems = [{
            agreement_to_service_rel_id: prepaymentRel.id,
            service_id: prepaymentRel.service_id,
            service_name: prepaymentRel.service?.name,
            quantity: 1,
            discount: 0,
            total_price: total
          }];
        }
      }
      
      setFormData({
        number: transaction.number,
        type: transaction.type,
        status: transaction.status,
        company_id: transaction.company_id,
        agreement_id: transaction.agreement_id,
        total: typeof transaction.total === 'number' ? transaction.total : parseFloat(String(transaction.total || 0)),
        confirmation_document_url: transaction.confirmation_document_url || '',
        confirmation_document_file: transaction.confirmation_document_file || '',
        field1: transaction.field1 || '',
        field2: transaction.field2 || '',
        field3: transaction.field3 || '',
        field4: transaction.field4 || '',
        field5: transaction.field5 || '',
        transaction_items: transactionItems
      });
      // Парсим confirmation_document_url как JSON массив или используем как строку
      try {
        const docs = transaction.confirmation_document_url 
          ? (typeof transaction.confirmation_document_url === 'string' && transaction.confirmation_document_url.startsWith('[')
            ? JSON.parse(transaction.confirmation_document_url)
            : [transaction.confirmation_document_url])
          : [];
        setConfirmationDocuments(docs.filter((d: string) => d && d.trim()));
      } catch {
        setConfirmationDocuments(transaction.confirmation_document_url ? [transaction.confirmation_document_url] : []);
      }
      setConfirmationFiles([]); // Сбрасываем загружаемые файлы
      setOpenEditDialog(true);
    }
  };

  const handleAddTransaction = () => {
    setSelectedTransaction(null);
    setIsPrepaymentMode(false);
    setIsNewTransaction(true); // Это новая транзакция
    setShowCreatePreview(false);
    setSendEmailOnCreate(false);
    
    // Находим услугу Prepayment для автоматического добавления при выборе prepayment
    const prepaymentService = services.find(s => s.identifier === 'prepayment');
    
    setFormData({
      number: '',
      type: 'invoice', // По умолчанию invoice, но пользователь может выбрать prepayment
      status: 'new',
      company_id: undefined,
      agreement_id: undefined,
      total: 0,
      confirmation_document_url: '',
      field1: '',
      field2: '',
      field3: '',
      field4: '',
      field5: '',
      transaction_items: [] // Для invoice услуги будут добавлены после выбора договора
    });
    setConfirmationDocuments([]);
    setConfirmationFiles([]);
    setOpenEditDialog(true);
  };

  const handleMakePrepayment = () => {
    setSelectedTransaction(null);
    setIsPrepaymentMode(true);
    setIsNewTransaction(true); // Это новая транзакция
    setShowCreatePreview(false);
    setSendEmailOnCreate(false);
    setFormData({
      number: '',
      type: 'prepayment',
      status: 'approved', // Для prepayment автоматически устанавливаем статус approved
      company_id: undefined,
      agreement_id: undefined,
      total: 0,
      confirmation_document_url: '',
      field1: '',
      field2: '',
      field3: '',
      field4: '',
      field5: '',
      transaction_items: [] // Не добавляем услугу сразу, добавим после выбора договора
    });
    setOpenEditDialog(true);
  };

  const handleTransactionItemChange = (index: number, field: keyof TransactionItemForm, value: any) => {
    const newItems = [...formData.transaction_items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'agreement_to_service_rel_id' && value && formData.agreement_id) {
      const serviceRelation = agreementServiceRelations.find(rel => rel.id === value);
      if (serviceRelation) {
        newItems[index].service_id = serviceRelation.service_id;
        newItems[index].service_name = serviceRelation.service?.name;
        newItems[index].quantity = 1;
        if (formData.type === 'invoice') {
          newItems[index].discount = 0;
        }
      }
    }
    
    setFormData({ ...formData, transaction_items: newItems });
  };

  const handleAddTransactionItem = () => {
    setFormData({
      ...formData,
      transaction_items: [...formData.transaction_items, { agreement_to_service_rel_id: 0, quantity: 1, discount: 0, total_price: undefined }]
    });
  };

  const handleRemoveTransactionItem = (index: number) => {
    // Для транзакций типа prepayment нельзя удалять связь с Prepayment
    if (formData.type === 'prepayment') {
      const item = formData.transaction_items[index];
      const rel = agreementServiceRelations.find(r => r.id === item.agreement_to_service_rel_id);
      const service = rel?.service || services.find(s => s.id === item.service_id);
      if (service?.identifier === 'prepayment') {
        return; // Не позволяем удалять Prepayment
      }
    }
    
    const newItems = formData.transaction_items.filter((_, i) => i !== index);
    setFormData({ ...formData, transaction_items: newItems });
  };

  const handleProceedToPreview = async () => {
    const validationError = validateNewTransactionForm(formData);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    if (formData.company_id) {
      try {
        const wallets = await companyWalletsApi.getCompanyWallets(formData.company_id);
        setCompanyWallets(wallets);
      } catch (err) {
        console.error('Error loading company wallets for preview:', err);
        setCompanyWallets([]);
      }
    } else {
      setCompanyWallets([]);
    }
    setShowCreatePreview(true);
  };

  const handleBackFromPreview = () => {
    setShowCreatePreview(false);
    setError('');
  };

  const handleSaveTransaction = async () => {
    try {
      setFormLoading(true);
      setError('');

      if (isNewTransaction) {
        const validationError = validateNewTransactionForm(formData);
        if (validationError) {
          setError(validationError);
          setFormLoading(false);
          return;
        }
      } else if (formData.type === 'invoice') {
        const hasSelectedService = formData.transaction_items.some(
          (item) => item.agreement_to_service_rel_id && Number(item.agreement_to_service_rel_id) > 0
        );
        if (!hasSelectedService) {
          setError('At least one service must be selected for the invoice.');
          setFormLoading(false);
          return;
        }
      }

      const calculatedTotal = calculateTransactionTotal(formData, agreementServiceRelations);
      
      // Определяем статус:
      // - Для новых транзакций: автоматически (prepayment -> approved, invoice -> new)
      // - Для редактирования существующих: используем статус из формы
      let transactionStatus: string;
      if (selectedTransaction) {
        // При редактировании используем статус из формы
        transactionStatus = formData.status || 'new';
      } else {
        // При создании автоматически устанавливаем статус:
        // - prepayment -> approved
        // - invoice -> new
        transactionStatus = formData.type === 'prepayment' ? 'approved' : 'new';
      }
      
        const transactionData: any = {
        type: formData.type,
        status: transactionStatus,
        company_id: formData.company_id || undefined,
        agreement_id: formData.agreement_id || undefined,
        total: calculatedTotal || formData.total || 0, // Передаем total для автоматического добавления Prepayment
        confirmation_document_url: confirmationDocuments.length > 0 ? JSON.stringify(confirmationDocuments) : undefined,
        confirmation_document_file: formData.confirmation_document_file || undefined,
        field1: formData.field1 || undefined,
        field2: formData.field2 || undefined,
        field3: formData.field3 || undefined,
        field4: formData.field4 || undefined,
        field5: formData.field5 || undefined,
        send_email:
          isNewTransaction && formData.type === 'invoice' && sendEmailOnCreate,
        transaction_items: formData.transaction_items.map(item => {
          const payload: { agreement_to_service_rel_id: number; quantity: number; discount?: number; total_price?: number } = {
            agreement_to_service_rel_id: item.agreement_to_service_rel_id,
            quantity: lineQuantity(item)
          };
          if (formData.type === 'prepayment') {
            const tp = item.total_price ?? 0;
            payload.total_price = typeof tp === 'number' ? tp : parseFloat(String(tp || 0));
          } else {
            payload.discount = typeof item.discount === 'number' ? item.discount : parseFloat(String(item.discount || 0));
          }
          return payload;
        })
      };
      
      if (selectedTransaction) {
        // При обновлении отправляем number
        transactionData.number = formData.number;
        
        // Если есть новые файлы для загрузки, загружаем их
        if (confirmationFiles.length > 0) {
          await handleUploadFiles(selectedTransaction.id, confirmationFiles);
        }
        
        await transactionsApi.updateTransaction(selectedTransaction.id, transactionData);
      } else {
        // При создании number не отправляем - будет сгенерирован автоматически на бэкенде
        // Если тип prepayment и указан договор, бэкенд автоматически добавит услугу Prepayment
        const createdTransaction = await transactionsApi.createTransaction(transactionData);
        
        if (createdTransaction.field4) {
          setSuccessMessage(
            `Invoice created. Stripe payment link: ${createdTransaction.field4}`
          );
        }
        
        // Если есть файлы для загрузки, загружаем их после создания транзакции
        if (confirmationFiles.length > 0 && createdTransaction.id) {
          await handleUploadFiles(createdTransaction.id, confirmationFiles);
        }
      }
      
      setOpenEditDialog(false);
      setIsPrepaymentMode(false);
      setIsNewTransaction(false);
      setShowCreatePreview(false);
              setConfirmationFiles([]);
              setConfirmationDocuments([]);
      await loadTransactions();
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Error saving transaction');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSendForApproval = async () => {
    if (!selectedTransaction) return;

    const companyId = selectedTransaction.company_id ?? formData.company_id;
    const company = companyId ? companies.find((c: { id?: number }) => c.id === companyId) : null;
    const paymentMethod = company?.payment_method;

    if (paymentMethod === 'crypto_transfer') {
      if (!formData.field1?.trim()) {
        setError('For crypto transfer, please enter the transaction hash with link in the Comment field');
        return;
      }
    } else {
      if (confirmationDocuments.length === 0 && confirmationFiles.length === 0) {
        setError('For bank transfer, at least one confirmation document must be uploaded');
        return;
      }
    }

    try {
      setFormLoading(true);
      setError('');
      
      const calculatedTotal = formData.transaction_items.reduce((sum, item) => {
        if (formData.type === 'prepayment') {
          const tp = item.total_price ?? 0;
          return sum + (typeof tp === 'number' ? tp : parseFloat(String(tp || 0)));
        }
        const rel = agreementServiceRelations.find(r => r.id === item.agreement_to_service_rel_id);
        const price = rel ? (typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0))) : 0;
        const discountVal = typeof item.discount === 'number' ? item.discount : parseFloat(String(item.discount || 0));
        const q = lineQuantity(item);
        return sum + Math.max(0, price * q - discountVal);
      }, 0);
      
      const transactionData: any = {
        type: formData.type,
        company_id: formData.company_id || undefined,
        agreement_id: formData.agreement_id || undefined,
        total: calculatedTotal || formData.total || 0,
        confirmation_document_url: confirmationDocuments.length > 0 ? JSON.stringify(confirmationDocuments) : undefined,
        confirmation_document_file: formData.confirmation_document_file || undefined,
        field1: formData.field1?.trim() || undefined,
        field2: formData.field2 || undefined,
        field3: formData.field3 || undefined,
        field4: formData.field4 || undefined,
        field5: formData.field5 || undefined,
        transaction_items: formData.transaction_items.map(item => {
          const payload: { agreement_to_service_rel_id: number; quantity: number; discount?: number; total_price?: number } = {
            agreement_to_service_rel_id: item.agreement_to_service_rel_id,
            quantity: lineQuantity(item)
          };
          if (formData.type === 'prepayment') {
            const tp = item.total_price ?? 0;
            payload.total_price = typeof tp === 'number' ? tp : parseFloat(String(tp || 0));
          } else {
            payload.discount = typeof item.discount === 'number' ? item.discount : parseFloat(String(item.discount || 0));
          }
          return payload;
        })
      };
      
      transactionData.number = formData.number;
      
      // Если есть новые файлы для загрузки, загружаем их
      if (confirmationFiles.length > 0) {
        await handleUploadFiles(selectedTransaction.id, confirmationFiles);
      }
      
      await transactionsApi.updateTransaction(selectedTransaction.id, transactionData);
      
      // Отправляем на подтверждение (NEW -> PAYED)
      await transactionsApi.sendForApproval(selectedTransaction.id);
      
      setOpenEditDialog(false);
      setIsPrepaymentMode(false);
      setIsNewTransaction(false);
      setConfirmationFiles([]);
      setConfirmationDocuments([]);
      await loadTransactions();
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Error sending transaction for approval');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files).slice(0, 5); // Максимум 5 файлов
      setConfirmationFiles(files);
    }
  };

  const handleUploadFiles = async (transactionId: number, files: File[]) => {
    try {
      setUploadingFile(true);
      setError('');
      
      const uploadedUrls: string[] = [];
      
      for (const file of files) {
        try {
          const result = await transactionsApi.uploadConfirmationFile(transactionId, file);
          const url = (result as { file_url?: string; file_path?: string }).file_url ?? (result as { file_path?: string }).file_path;
          if (url) {
            uploadedUrls.push(url);
          }
        } catch (err: any) {
          console.error(`Error uploading file ${file.name}:`, err);
          setError(`Error uploading ${file.name}: ${formatApiError(err.response?.data?.detail) || 'Unknown error'}`);
        }
      }
      
      if (uploadedUrls.length > 0) {
        // Добавляем новые ссылки к существующим
        setConfirmationDocuments(prev => {
          const allDocuments = [...prev, ...uploadedUrls];
          
          // Обновляем транзакцию с новыми ссылками
          transactionsApi.updateTransaction(transactionId, {
            confirmation_document_url: JSON.stringify(allDocuments)
          }).catch(err => {
            console.error('Error updating transaction with documents:', err);
          });
          
          // Обновляем форму
          setFormData(prevForm => ({
            ...prevForm,
            confirmation_document_url: JSON.stringify(allDocuments)
          }));
          
          return allDocuments;
        });
      }
      
      setConfirmationFiles([]); // Очищаем выбранные файлы после загрузки
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Error uploading files');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveConfirmationDocument = async (indexToRemove: number) => {
    if (!selectedTransaction || formData.status !== 'new') return;
    const newList = confirmationDocuments.filter((_, i) => i !== indexToRemove);
    setConfirmationDocuments(newList);
    setFormData(prev => ({ ...prev, confirmation_document_url: newList.length > 0 ? JSON.stringify(newList) : '' }));
    try {
      await transactionsApi.updateTransaction(selectedTransaction.id, {
        confirmation_document_url: newList.length > 0 ? JSON.stringify(newList) : ''
      });
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to remove document');
      setConfirmationDocuments(confirmationDocuments);
      setFormData(prev => ({ ...prev, confirmation_document_url: confirmationDocuments.length > 0 ? JSON.stringify(confirmationDocuments) : '' }));
    }
  };

  const handleDownloadPdf = async (transactionId: number, transactionType: string) => {
    // Download PDF for invoices and prepayments
    if (transactionType !== 'invoice' && transactionType !== 'prepayment') {
      setError('PDF can only be downloaded for invoices and prepayments');
      return;
    }
    
    try {
      setError('');
      await transactionsApi.downloadTransactionPdf(transactionId);
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Error downloading PDF');
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '-';
    return `$${(typeof amount === 'number' ? amount : parseFloat(String(amount || 0))).toFixed(2)}`;
  };

  if (authLoading || (loading && transactions.length === 0 && !txShellOnlyUser)) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {txShellOnlyUser ? (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddTransaction}>
            Add Transaction
          </Button>
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Transactions
          </Typography>
          <Box display="flex" gap={2}>
            {!isClient && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddTransaction}
              >
                Add Transaction
              </Button>
            )}
            {canBulkGenerate && (
              <Button
                variant="outlined"
                onClick={() => setBulkDialogOpen(true)}
              >
                Bulk generate invoices
              </Button>
            )}
            {canBulkGenerate && (
              <Button variant="outlined" onClick={() => setReportDialogOpen(true)}>
                Report
              </Button>
            )}
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {typeof error === 'string' ? error : formatApiError(error)}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {!txShellOnlyUser && (
      <>
      <Box display="flex" flexWrap="wrap" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search by company or invoice number..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <TextField
          label="Created from"
          type="date"
          size="small"
          value={filterDateFrom}
          onChange={(e) => {
            const v = e.target.value;
            setFilterDateFrom(v);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Created to"
          type="date"
          size="small"
          value={filterDateTo}
          onChange={(e) => {
            const v = e.target.value;
            setFilterDateTo(v);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(0);
            }}
            label="Type"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="prepayment">Prepayment</MenuItem>
            <MenuItem value="invoice">Invoice</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="payed">Paid</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            setSearchTerm('');
            setFilterType('');
            setFilterStatus('');
            setFilterDateFrom('');
            setFilterDateTo('');
            setPage(0);
          }}
        >
          Reset
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Agreement</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      sx={{ 
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handleOpenTransaction(transaction.id)}
                    >
                      {transaction.number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={typeLabels[transaction.type] || transaction.type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{transaction.company_name || '-'}</TableCell>
                  <TableCell>{transaction.agreement_number || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatAmount(transaction.total)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[transaction.status] || transaction.status}
                      color={statusColors[transaction.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(transaction.created_date)}</TableCell>
                  <TableCell>
                    {(transaction.type === 'invoice' || transaction.type === 'prepayment') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadPdf(transaction.id, transaction.type)}
                        title={transaction.type === 'prepayment' ? 'Download Prepayment' : 'Download Invoice'}
                        sx={{ mr: 0.5 }}
                        color="primary"
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Rows per page:"
      />
      </>
      )}

      {/* Edit/Create/View Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setIsPrepaymentMode(false);
          setIsNewTransaction(false);
          setShowCreatePreview(false);
          setError('');
          // Не сбрасываем реквизиты при закрытии, чтобы они не исчезали
          // Не сбрасываем isViewMode для клиентов, чтобы избежать кратковременного отображения редактируемых полей и кнопки Save
          if (!isClient) {
            setIsViewMode(false);
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {showCreatePreview
            ? 'Review Transaction'
            : isPrepaymentMode
              ? 'Make Prepayment'
              : selectedTransaction
                ? (isViewMode ? 'View Transaction' : 'Edit Transaction')
                : 'Add Transaction'}
        </DialogTitle>
        <DialogContent>
          {formLoading && !selectedTransaction && !showCreatePreview ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : showCreatePreview && isNewTransaction ? (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please review the transaction details before saving.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Type"
                    value={typeLabels[formData.type] || formData.type}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={statusLabels[formData.type === 'prepayment' ? 'approved' : 'new']}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company"
                    value={(() => {
                      const company = companies.find(c => c.id === formData.company_id);
                      return company
                        ? (company.country ? `${company.name} (${company.country})` : company.name)
                        : 'Not selected';
                    })()}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Agreement"
                    value={(() => {
                      const agreement = agreements.find(a => a.id === formData.agreement_id);
                      return agreement ? agreement.number : 'Not selected';
                    })()}
                    disabled
                  />
                </Grid>
                {formData.type === 'invoice' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Send email"
                      value={sendEmailOnCreate ? 'Yes' : 'No'}
                      disabled
                    />
                  </Grid>
                )}
                {formData.field1?.trim() && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Comment"
                      value={formData.field1}
                      disabled
                      multiline
                      rows={2}
                    />
                  </Grid>
                )}
                {(formData.type === 'invoice' || formData.type === 'prepayment') && (
                  <Grid item xs={12}>
                    <InvoiceDocumentPreview
                      companyName={(() => {
                        const company = companies.find((c) => c.id === formData.company_id);
                        return company?.name || 'Client';
                      })()}
                      paymentMethod={(() => {
                        const company = companies.find((c) => c.id === formData.company_id);
                        return company?.payment_method;
                      })()}
                      companyWallets={companyWallets}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Services
                      </Typography>
                      {formData.transaction_items.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Service</TableCell>
                                {formData.type === 'invoice' && (
                                  <TableCell align="right" width={100}>Price</TableCell>
                                )}
                                <TableCell align="right" width={80}>Qty</TableCell>
                                <TableCell align="right" width={120}>
                                  {formData.type === 'prepayment' ? 'Amount' : 'Discount'}
                                </TableCell>
                                <TableCell align="right" width={120}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {formData.transaction_items.map((item, index) => {
                                const rel = agreementServiceRelations.find(
                                  (r) => r.id === item.agreement_to_service_rel_id
                                );
                                const lineQty = lineQuantity(item);
                                const servicePrice = rel
                                  ? (typeof rel.price === 'number'
                                    ? rel.price
                                    : parseFloat(String(rel.price || 0)))
                                  : 0;
                                const discount =
                                  typeof item.discount === 'number'
                                    ? item.discount
                                    : parseFloat(String(item.discount || 0));
                                const totalPricePrepayment =
                                  typeof item.total_price === 'number'
                                    ? item.total_price
                                    : parseFloat(String(item.total_price ?? 0));
                                const itemTotal =
                                  formData.type === 'prepayment'
                                    ? totalPricePrepayment
                                    : Math.max(0, servicePrice * lineQty - discount);
                                const service = item.service_name
                                  ? { name: item.service_name }
                                  : rel?.service || services.find((s) => s.id === item.service_id);
                                return (
                                  <TableRow key={index}>
                                    <TableCell>
                                      {service?.name ||
                                        `Service ID: ${item.service_id || item.agreement_to_service_rel_id}`}
                                    </TableCell>
                                    {formData.type === 'invoice' && (
                                      <TableCell align="right">{formatAmount(servicePrice)}</TableCell>
                                    )}
                                    <TableCell align="right">{lineQty}</TableCell>
                                    <TableCell align="right">
                                      {formatAmount(
                                        formData.type === 'prepayment' ? totalPricePrepayment : discount
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{formatAmount(itemTotal)}</TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow>
                                <TableCell colSpan={formData.type === 'invoice' ? 4 : 3}>
                                  <Typography variant="body2" fontWeight={600}>
                                    Total amount
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatAmount(
                                      calculateTransactionTotal(formData, agreementServiceRelations)
                                    )}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No items
                        </Typography>
                      )}
                    </InvoiceDocumentPreview>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {isViewMode && selectedTransaction && (
                  <>
                <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="ID"
                        value={selectedTransaction.id}
                        disabled
                      />
                </Grid>
                <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Number"
                        value={selectedTransaction.number}
                        disabled
                      />
                </Grid>
                <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Total Amount"
                        value={formatAmount(selectedTransaction.total)}
                        disabled
                      />
                </Grid>
                    {selectedTransaction.created_date && (
                <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Created"
                          value={formatDateTime(selectedTransaction.created_date)}
                          disabled
                        />
                  </Grid>
                )}
                    {selectedTransaction.updated_date && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Updated"
                          value={formatDateTime(selectedTransaction.updated_date)}
                          disabled
                        />
                      </Grid>
                    )}
                    {selectedTransaction.canceled_date && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Canceled"
                          value={formatDateTime(selectedTransaction.canceled_date)}
                          disabled
                        />
                      </Grid>
                    )}
                  </>
                )}
                {isViewMode && selectedTransaction ? (
                  <>
                <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transaction Number"
                        value={selectedTransaction.number || '-'}
                        disabled
                      />
                </Grid>
                <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Type"
                        value={typeLabels[selectedTransaction.type] || selectedTransaction.type}
                        disabled
                      />
                </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Status"
                        value={statusLabels[selectedTransaction.status] || selectedTransaction.status}
                        disabled
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
        fullWidth
                        label="Company"
                        value={selectedTransaction.company_id ? (() => {
                          const company = companies.find(c => c.id === selectedTransaction.company_id);
                          return company ? (company.country ? `${company.name} (${company.country})` : company.name) : 'Not selected';
                        })() : 'Not selected'}
                        disabled
                      />
                    </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                        label="Agreement"
                        value={selectedTransaction.agreement_id ? (() => {
                          const agreement = agreements.find(a => a.id === selectedTransaction.agreement_id);
                          return agreement ? agreement.number : 'Not selected';
                        })() : 'Not selected'}
                        disabled
                  />
                </Grid>
                  </>
                ) : (
                  <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Transaction Number"
                    value={formData.number || (selectedTransaction?.number || '-')}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                        <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={async (e) => {
                        const newType = e.target.value as any;
                        const oldType = formData.type;
                        let newTransactionItems: TransactionItemForm[] = [];
                        
                        if (oldType === 'prepayment' && newType === 'invoice') {
                          // Переключение на Invoice — сбрасываем позиции (Prepayment убирается)
                          newTransactionItems = [];
                        } else if (oldType === 'invoice' && newType === 'prepayment') {
                          // Переключение на Prepayment — добавляем услугу Prepayment автоматически, если есть договор
                          if (formData.agreement_id) {
                            const relations = await loadAgreementServices(formData.agreement_id);
                            const prepaymentRel = relations.find((r: any) => r.service?.identifier === 'prepayment');
                            if (prepaymentRel) {
                              newTransactionItems = [{
                                agreement_to_service_rel_id: prepaymentRel.id,
                                service_id: prepaymentRel.service_id,
                                service_name: prepaymentRel.service?.name,
                                quantity: 1,
                                discount: 0,
                                total_price: 0
                              }];
                            }
                          }
                        }
                        
                        setFormData({ 
                          ...formData, 
                          type: newType,
                          transaction_items: newTransactionItems
                        });
                        
                        if (newType === 'invoice' && formData.agreement_id) {
                          await loadAgreementServices(formData.agreement_id);
                        } else if (newType !== 'prepayment') {
                          setAgreementServices([]);
                          setAgreementServiceRelations([]);
                        }
                      }}
                          label="Type"
                          disabled={isPrepaymentMode || isClient || isViewMode || !isNewTransaction} // Можно редактировать только при создании новой транзакции
                    >
                          <MenuItem value="prepayment">Prepayment</MenuItem>
                          <MenuItem value="invoice">Invoice</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                        <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                          label="Status"
                          disabled={isClient || isViewMode || (isNewTransaction && formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id)) || (isNewTransaction && formData.type === 'prepayment' && !formData.company_id)}
                    >
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="payed">Paid</MenuItem>
                          <MenuItem value="approved">Approved</MenuItem>
                          <MenuItem value="canceled">Canceled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                        <InputLabel>Company</InputLabel>
                    <Select
                      value={formData.company_id || ''}
                      onChange={async (e) => {
                        const companyId = e.target.value ? (e.target.value as number) : undefined;
                        // Если сбросили компанию, сбрасываем договор и услуги
                        if (!companyId) {
                          setFormData(prev => ({ 
                            ...prev, 
                            company_id: undefined, 
                            agreement_id: undefined,
                            transaction_items: []
                          }));
                          setAgreementServices([]);
                          setAgreementServiceRelations([]);
                          setAgreements([]);
                        } else {
                          setFormData(prev => ({ ...prev, company_id: companyId, agreement_id: undefined }));
                          // Для клиентов загружаем договоры выбранной компании
                          if (isClient) {
                            try {
                              const data = await agreementsApi.getAgreementsByCompany(companyId);
                              setAgreements(data);
                            } catch (err: any) {
                              console.error('Error loading agreements:', err);
                            }
                          }
                        }
                      }}
                          label="Company"
                          disabled={isClient || isViewMode || !isNewTransaction} // Можно редактировать только при создании новой транзакции
                    >
                          <MenuItem value="">Not selected</MenuItem>
                      {(isCsmUser ? companiesForCsm(companies) : companies).map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.country ? `${company.name} (${company.country})` : company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                        <InputLabel>Agreement</InputLabel>
                    <Select
                      value={formData.agreement_id || ''}
                      onChange={async (e) => {
                        const agreementId = e.target.value ? (e.target.value as number) : undefined;
                        
                        if (agreementId && formData.type === 'invoice') {
                          await loadAgreementServices(agreementId);
                          setFormData(prev => ({ ...prev, agreement_id: agreementId }));
                        } else if (agreementId && formData.type === 'prepayment') {
                          const relations = await loadAgreementServices(agreementId);
                          const prepaymentRel = relations.find((r: any) => r.service?.identifier === 'prepayment');
                          const newItems = prepaymentRel ? [{
                            agreement_to_service_rel_id: prepaymentRel.id,
                            service_id: prepaymentRel.service_id,
                            service_name: prepaymentRel.service?.name,
                            quantity: 1,
                            discount: 0,
                            total_price: 0
                          }] : [];
                          setFormData(prev => ({ ...prev, agreement_id: agreementId, transaction_items: newItems }));
                        } else {
                          setAgreementServices([]);
                          setAgreementServiceRelations([]);
                          setFormData(prev => ({ ...prev, agreement_id: agreementId, transaction_items: [] }));
                        }
                      }}
                          label="Agreement"
                          disabled={isClient || isViewMode || !isNewTransaction || (isNewTransaction && !formData.company_id)} // Можно редактировать только при создании новой транзакции
                    >
                          <MenuItem value="">Not selected</MenuItem>
                      {agreements
                        .filter(agreement => {
                          const companyMatch =
                            !formData.company_id || agreement.company_id === formData.company_id;
                          if (!companyMatch) return false;
                          if (!isNewTransaction) return true;
                          return isAgreementSelectable(agreement);
                        })
                        .map((agreement) => (
                          <MenuItem key={agreement.id} value={agreement.id}>
                            {agreement.number}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                {!isClient && isNewTransaction && formData.type === 'invoice' && !isViewMode && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendEmailOnCreate}
                          onChange={(_, c) => setSendEmailOnCreate(c)}
                        />
                      }
                      label="Send email"
                    />
                  </Grid>
                )}
                  </>
                )}
                {/* Confirmation Documents - показываем всегда, но редактируем только для статуса new */}
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Confirmation Documents
                    </Typography>
                    {confirmationDocuments.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        {confirmationDocuments.map((docUrl, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                            <Button
                              size="small"
                              onClick={async () => {
                                if (!selectedTransaction) return;
                                try {
                                  await transactionsApi.downloadConfirmationFile(selectedTransaction.id, index, docUrl.split('/').pop() || undefined);
                                } catch (err: any) {
                                  setError(formatApiError(err.response?.data?.detail) || 'Failed to download document');
                                }
                              }}
                              sx={{ flex: 1, justifyContent: 'flex-start', textTransform: 'none', color: 'primary.main' }}
                            >
                              <Download fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" component="span" noWrap sx={{ color: 'inherit' }}>
                                {docUrl.split('/').pop() || docUrl}
                              </Typography>
                            </Button>
                            {!isViewMode && formData.status === 'new' && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveConfirmationDocument(index)}
                                title="Remove document"
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No documents uploaded
                      </Typography>
                    )}
                    {!isNewTransaction && formData.status === 'new' && (
                      <Box>
                        <input
                          accept="*/*"
                          style={{ display: 'none' }}
                          id="confirmation-file-upload"
                          type="file"
                          multiple
                          onChange={async (e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              const files = Array.from(e.target.files).slice(0, 5); // Максимум 5 файлов
                              setConfirmationFiles(files);
                              
                              // Если транзакция уже создана, загружаем файлы сразу
                              if (selectedTransaction && selectedTransaction.status === 'new') {
                                await handleUploadFiles(selectedTransaction.id, files);
                              }
                            }
                          }}
                          disabled={isViewMode || uploadingFile}
                        />
                        <label htmlFor="confirmation-file-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            disabled={isViewMode || uploadingFile}
                            startIcon={<Upload />}
                            fullWidth
                          >
                            {uploadingFile 
                              ? 'Uploading...' 
                              : confirmationFiles.length > 0 
                                ? `${confirmationFiles.length} file(s) selected (max 5)` 
                                : 'Upload Confirmation Files (up to 5)'}
                          </Button>
                        </label>
                        {confirmationFiles.length > 0 && !uploadingFile && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {confirmationFiles.map(f => f.name).join(', ')}
                          </Typography>
                        )}
                        {isClient && confirmationDocuments.length === 0 && confirmationFiles.length === 0 && (() => {
                          const txCompanyId = selectedTransaction?.company_id ?? formData.company_id;
                          const txCompany = txCompanyId ? companies.find((c: { id?: number }) => c.id === txCompanyId) : null;
                          const isCryptoTransfer = txCompany?.payment_method === 'crypto_transfer';
                          if (isCryptoTransfer) {
                            return (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                For Crypto transfer, confirmation is provided in the Comment field (transaction hash with link).
                              </Typography>
                            );
                          }
                          return (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              Required: At least one document must be uploaded
                            </Typography>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                </Grid>
                {!isNewTransaction && formData.type === 'invoice' && (() => {
                  const txCompanyId = selectedTransaction?.company_id ?? formData.company_id;
                  const txCompany = txCompanyId ? companies.find((c: { id?: number }) => c.id === txCompanyId) : null;
                  if (txCompany?.payment_method !== 'bank_transfer') return null;
                  return (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Stripe Invoice ID"
                          value={formData.field3}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Payment Link"
                          value={formData.field4}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                        {formData.field4 && (
                          <Link
                            href={formData.field4}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                          >
                            Open payment page <OpenInNew fontSize="inherit" />
                          </Link>
                        )}
                      </Grid>
                    </>
                  );
                })()}
                <Grid item xs={12}>
                  {(() => {
                    const txCompanyId = selectedTransaction?.company_id ?? formData.company_id;
                    const txCompany = txCompanyId ? companies.find((c: { id?: number }) => c.id === txCompanyId) : null;
                    const isCryptoTransfer = txCompany?.payment_method === 'crypto_transfer';
                    return (
                      <TextField
                        fullWidth
                        label="Comment"
                        value={formData.field1}
                        onChange={(e) => setFormData({ ...formData, field1: e.target.value })}
                        multiline
                        rows={2}
                        disabled={isViewMode || formData.status === 'canceled' || formData.status === 'approved'}
                        helperText={isClient && formData.status === 'new' && isCryptoTransfer
                          ? 'To confirm crypto payment, enter the transaction hash with link in the Comment field (e.g. a block explorer link).'
                          : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">
                      <strong>Transaction Items</strong>
                    </Typography>
                    {!isClient && !isViewMode && isNewTransaction && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddTransactionItem}
                      disabled={(formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id)) || (formData.type === 'prepayment' && !formData.company_id)}
                    >
                        Add Item
                    </Button>
                    )}
                  </Box>
                  {((isViewMode && selectedTransaction?.transaction_items && selectedTransaction.transaction_items.length > 0) || (!isViewMode && formData.transaction_items.length > 0)) ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell align="right" width={100}>Qty</TableCell>
                            <TableCell align="right" width={150}>{formData.type === 'prepayment' ? 'Amount' : 'Discount'}</TableCell>
                            <TableCell align="right" width={120}>Total</TableCell>
                            {!isViewMode && isNewTransaction && <TableCell width={50}></TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(isViewMode && selectedTransaction?.transaction_items 
                            ? selectedTransaction.transaction_items 
                            : formData.transaction_items
                          ).map((item, index) => {
                            const rel = agreementServiceRelations.find(r => r.id === item.agreement_to_service_rel_id);
                            const lineQty = lineQuantity(item);
                            const relFromItem = (item as TransactionItem).agreement_to_service_rel;
                            const servicePrice = rel
                              ? (typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0)))
                              : relFromItem
                                ? (typeof relFromItem.price === 'number' ? relFromItem.price : parseFloat(String(relFromItem.price || 0)))
                                : 0;
                            const discount = typeof item.discount === 'number' ? item.discount : parseFloat(String((item as any).discount ?? 0));
                            const totalFromItem = typeof (item as any).total_price === 'number' ? (item as any).total_price : parseFloat(String((item as any).total_price ?? 0));
                            const totalPricePrepayment = typeof (item as any).total_price === 'number' ? (item as any).total_price : parseFloat(String((item as any).total_price ?? 0));
                            const itemTotal = isViewMode
                              ? totalFromItem
                              : formData.type === 'prepayment'
                                ? totalPricePrepayment
                                : Math.max(0, servicePrice * lineQty - discount);
                            // Для отображения получаем услугу из связи или напрямую
                            const service = isViewMode && selectedTransaction?.transaction_items?.[index]
                              ? (selectedTransaction.transaction_items[index].agreement_to_service_rel?.service || selectedTransaction.transaction_items[index].service)
                              : ('service_name' in item && item.service_name
                                  ? { name: item.service_name, id: item.service_id }
                                  : (() => {
                                      const rel = agreementServiceRelations.find(r => r.id === item.agreement_to_service_rel_id);
                                      return rel?.service || services.find(s => s.id === item.service_id);
                                    })()
                                );
                            const isPrepaymentServiceRow = formData.type === 'prepayment' && service?.identifier === 'prepayment';
                            const showServiceReadOnly = isViewMode || !isNewTransaction || isPrepaymentServiceRow;
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  {showServiceReadOnly ? (
                                    <Typography variant="body2">
                                      {service?.name || `Service ID: ${item.service_id || item.agreement_to_service_rel_id}`}
                                    </Typography>
                                  ) : (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={item.agreement_to_service_rel_id || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '') return;
                                        handleTransactionItemChange(index, 'agreement_to_service_rel_id', Number(val));
                                      }}
                                      disabled={isClient || isViewMode || (() => {
                                        if (formData.type === 'invoice') {
                                          return !formData.company_id || !formData.agreement_id;
                                        }
                                        if (formData.type === 'prepayment') {
                                          return !formData.company_id || !formData.agreement_id;
                                        }
                                        return false;
                                      })()}
                                    >
                                        <MenuItem value="" disabled>Select service</MenuItem>
                                      {formData.type === 'invoice' && formData.agreement_id && agreementServiceRelations.length > 0
                                        ? (() => {
                                            const selectedRelIds = formData.transaction_items
                                              .map((item, idx) => idx !== index ? item.agreement_to_service_rel_id : null)
                                              .filter(id => id && id > 0);
                                            // Для Invoice нельзя добавлять Prepayment — только для типа Prepayment
                                            return agreementServiceRelations
                                              .filter(rel => rel.service?.identifier !== 'prepayment')
                                              .filter(rel => !selectedRelIds.includes(rel.id) || rel.id === item.agreement_to_service_rel_id)
                                              .map((rel) => (
                                                <MenuItem key={rel.id} value={rel.id}>
                                                  {rel.service?.name || `Service ${rel.service_id}`} - ${typeof rel.price === 'number' ? rel.price.toFixed(2) : rel.price}
                                                </MenuItem>
                                              ));
                                          })()
                                        : formData.type === 'prepayment' && formData.agreement_id
                                        ? (() => {
                                            // Получаем список уже выбранных agreement_to_service_rel_id (кроме текущего item)
                                            const selectedRelIds = formData.transaction_items
                                              .map((item, idx) => idx !== index ? item.agreement_to_service_rel_id : null)
                                              .filter(id => id && id > 0);
                                            
                                            // Фильтруем prepayment услуги, исключая уже выбранные
                                            return agreementServiceRelations
                                              .filter(rel => rel.service?.identifier === 'prepayment')
                                              .filter(rel => !selectedRelIds.includes(rel.id) || rel.id === item.agreement_to_service_rel_id)
                                              .map((rel) => (
                                                <MenuItem key={rel.id} value={rel.id}>
                                                  {rel.service?.name || 'Prepayment'} - ${typeof rel.price === 'number' ? rel.price.toFixed(2) : rel.price}
                                                </MenuItem>
                                              ));
                                          })()
                                        : [] // Если нет договора, не показываем услуги
                                      }
                                    </Select>
                                  </FormControl>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {(isViewMode || !isNewTransaction) ? (
                                    <Typography variant="body2" align="right">{lineQty}</Typography>
                                  ) : formData.type === 'prepayment' ? (
                                    <Typography variant="body2" align="right">1</Typography>
                                  ) : (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={lineQty}
                                      onChange={(e) => {
                                        const v = parseFloat(e.target.value);
                                        handleTransactionItemChange(
                                          index,
                                          'quantity',
                                          !Number.isNaN(v) && v > 0 ? v : 1
                                        );
                                      }}
                                      inputProps={{ min: '0.01', step: '0.01' }}
                                      sx={{ maxWidth: 100 }}
                                      disabled={
                                        isClient || isViewMode ||
                                        (formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id))
                                      }
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {(isViewMode || !isNewTransaction) ? (
                                    <Typography variant="body2" align="right">
                                      {formatAmount(formData.type === 'prepayment' ? totalPricePrepayment : discount)}
                                    </Typography>
                                  ) : (
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={formData.type === 'prepayment' ? totalPricePrepayment : discount}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value) || 0;
                                      handleTransactionItemChange(index, formData.type === 'prepayment' ? 'total_price' : 'discount', v);
                                    }}
                                    inputProps={{ min: '0', step: '0.01' }}
                                    fullWidth
                                    disabled={
                                      isClient || isViewMode ||
                                      ((formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id)) || (formData.type === 'prepayment' && !formData.company_id))
                                    }
                                  />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {formatAmount(itemTotal)}
                                </TableCell>
                                {!isClient && !isViewMode && isNewTransaction && (
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveTransactionItem(index)}
                                    color="error"
                                    disabled={
                                      // Для prepayment нельзя удалять связь с Prepayment
                                      (formData.type === 'prepayment' && 
                                      (() => {
                                        const rel = agreementServiceRelations.find(r => r.id === item.agreement_to_service_rel_id);
                                        const service = rel?.service || services.find(s => s.id === item.service_id);
                                        return service?.identifier === 'prepayment';
                                      })()) ||
                                      ((formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id)) || (formData.type === 'prepayment' && !formData.company_id))
                                    }
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    !isViewMode && (
                      <Alert severity="info">Items are optional but can be added</Alert>
                    )
                  )}
                </Grid>
                
                {/* Реквизиты для оплаты - показываем всегда, если есть */}
                {selectedTransaction && selectedTransaction.company_id && companyWallets.length > 0 && (
                  <Grid item xs={12}>
                    <Card sx={{ mt: 2, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: '#1a56db' }}>
                          Реквизиты для оплаты
                        </Typography>
                        <Grid container spacing={2}>
                          {companyWallets.map((companyWallet) => (
                            <Grid item xs={12} md={6} key={companyWallet.id}>
                              <Card variant="outlined" sx={{ height: '100%' }}>
                                <CardContent>
                                  {companyWallet.wallet?.wallet_type === 'BANK' && companyWallet.wallet?.bank_details && (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                        Банковские реквизиты:
                                      </Typography>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                        {companyWallet.wallet.bank_details}
                                      </Typography>
                                    </Box>
                                  )}
                                  {companyWallet.wallet?.wallet_type === 'CRYPTO' && companyWallet.wallet?.wallet_address && (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {companyWallet.wallet.crypto_currency} - {companyWallet.wallet.wallet_address}
                                      </Typography>
                                    </Box>
                                  )}
                                  {companyWallet.comment && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                      {companyWallet.comment}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
          {/* Проверка на незаполненные услуги только для новых транзакций в режиме редактирования формы */}
          {!showCreatePreview && isNewTransaction && formData.transaction_items.length > 0 && formData.transaction_items.some(item => !item.agreement_to_service_rel_id || item.agreement_to_service_rel_id === 0) && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setError('')}>
              Please select a service for all transaction items before saving.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setError('')}>
              {typeof error === 'string' ? error : formatApiError(error)}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
            {showCreatePreview && isNewTransaction && (
              <Button onClick={handleBackFromPreview} disabled={formLoading || uploadingFile}>
                Back
              </Button>
            )}
            <Button onClick={() => {
              setOpenEditDialog(false);
              setIsPrepaymentMode(false);
              setShowCreatePreview(false);
              setConfirmationFiles([]);
            setConfirmationDocuments([]);
              setError('');
              // Не сбрасываем реквизиты при закрытии, чтобы они не исчезали
              // Не сбрасываем isViewMode для клиентов, чтобы избежать кратковременного отображения редактируемых полей и кнопки Save
              if (!isClient) {
                setIsViewMode(false);
              }
            }} disabled={formLoading || uploadingFile}>
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <>
                {isClient && selectedTransaction && selectedTransaction.status === 'new' ? (
                  (() => {
                    const txCompanyId = selectedTransaction.company_id ?? formData.company_id;
                    const txCompany = txCompanyId ? companies.find((c: { id?: number }) => c.id === txCompanyId) : null;
                    const isCryptoTransfer = txCompany?.payment_method === 'crypto_transfer';
                    const canSendCrypto = !!formData.field1?.trim();
                    const canSendBank = confirmationDocuments.length > 0 || confirmationFiles.length > 0;
                    const canSend = isCryptoTransfer ? canSendCrypto : canSendBank;
                    return (
                      <Button
                        onClick={handleSendForApproval}
                        variant="contained"
                        color="primary"
                        disabled={formLoading || uploadingFile || !canSend}
                      >
                        {formLoading ? <CircularProgress size={20} /> : 'Send for Approving'}
                      </Button>
                    );
                  })()
                ) : !isClient ? (
                  showCreatePreview && isNewTransaction ? (
                    <Button
                      onClick={handleSaveTransaction}
                      variant="contained"
                      disabled={formLoading || uploadingFile}
                    >
                      {formLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  ) : isNewTransaction ? (
                    <Button
                      onClick={handleProceedToPreview}
                      variant="contained"
                      disabled={
                        formLoading ||
                        uploadingFile ||
                        isNewTransactionFormIncomplete(formData)
                      }
                    >
                      Review
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSaveTransaction}
                      variant="contained"
                      disabled={
                        formLoading ||
                        uploadingFile ||
                        (formData.type === 'invoice' && (!formData.company_id || !formData.agreement_id)) ||
                        (formData.type === 'prepayment' && !formData.company_id) ||
                        (formData.transaction_items.length > 0 && formData.transaction_items.some(item => !item.agreement_to_service_rel_id || item.agreement_to_service_rel_id === 0))
                      }
                    >
                      {formLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  )
                ) : null}
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <BulkGenerateInvoicesDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onCompleted={() => {
          void loadTransactions();
        }}
      />
      <TransactionReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
      />
    </Container>
  );
};

export { TransactionsPage };
